/* Lab 1.1 · 让机械臂够到杯子: forward and inverse kinematics, and the see-think-act loop.
   The page is index.html; this is its script, bundled to dist/main.js by `npm run build`.
   The loop is the brand's, in its colours: a camera samples the cup at 15 Hz (看, green), inverse
   kinematics turns the cup's position into three joint angles, drawn as a ghost arm (想, violet),
   and the motors drive the real arm after it (动, blue), as fast as they can. */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { C, q, recorded, createStage, floor, ring, run, toScreen, clamp, span, ease, el, type ScreenPoint } from "../kit/stage";
import { localizePage, tr, type Dict } from "../kit/i18n";
import STRINGS from "./i18n.json";

localizePage(STRINGS as Dict);

type Joints = [yaw: number, shoulder: number, elbow: number];
type Why = "ok" | "far" | "near" | "fk";
interface Floor { x: number; z: number }

const canvas = el<HTMLCanvasElement>("stage");
const { scene, camera, render } = createStage(canvas);
floor(scene);

// --- the arm: yaw at the base, then shoulder and elbow pitch; the wrist keeps the gripper pointing down
const H0 = .30, L1 = .36, L2 = .30, G = .11;          // shoulder height, links, wrist to fingertips (m)
const LIMITS: [number, number][] = [[-Math.PI, Math.PI], [-.35, 2.3], [-2.7, 0]];
const REST: Joints = [0, 1.25, -2.35];
const CUP_H = .1, GRASP_Y = .06;                     // the hand closes 6 cm up the cup
const WRIST_H = GRASP_Y + G - H0;                    // wrist height over the shoulder when grasping
const R_MAX = Math.sqrt((L1 + L2) ** 2 - WRIST_H ** 2), R_MIN = .15;
const DT = 1 / 240, SENSE_HZ = 15, SPEED = 6;

const metal = (color: number, metalness = .75, roughness = .4) => new THREE.MeshStandardMaterial({ color, metalness, roughness });
const glow = (color: number, k = 2.4) => new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: k, roughness: .4 });

interface Arm {
  root: THREE.Group;
  accents: THREE.Material[];
  set(qs: Joints, grip?: number): void;
}

// the real arm, or with a material given, its ghost: every part drawn in that one material
function makeArm(ghost?: THREE.Material): Arm {
  const shell = ghost ?? metal(0x2c3444), steel = ghost ?? metal(0x566274, .85, .32), dark = ghost ?? metal(0x121722, .5, .6);
  const accents = [0, 1, 2, 3].map(() => ghost ?? glow(C.act, 1));
  const mesh = (geo: THREE.BufferGeometry, mat: THREE.Material, cast = true): THREE.Mesh => {
    const m = new THREE.Mesh(geo, mat);
    m.castShadow = cast && !ghost;
    return m;
  };
  const cyl = (r: number, h: number, mat: THREE.Material, axis: "y" | "z" = "y"): THREE.Mesh => {
    const m = mesh(new THREE.CylinderGeometry(r, r, h, 48), mat);
    if (axis === "z") m.rotation.x = Math.PI / 2;
    return m;
  };
  const ringAt = (r: number, mat: THREE.Material, axis: "y" | "z" = "y"): THREE.Mesh => {
    const m = mesh(new THREE.TorusGeometry(r, .0045, 10, 64), mat, false);
    if (axis === "y") m.rotation.x = Math.PI / 2;
    return m;
  };
  const link = (len: number, r: number, mat: THREE.Material): THREE.Group => {   // a capsule along +x from the joint
    const g = new THREE.Group();
    const c = mesh(new THREE.CapsuleGeometry(r, len, 8, 32), mat);
    c.rotation.z = -Math.PI / 2;
    c.position.x = len / 2;
    g.add(c);
    for (const s of [-1, 1]) {   // side plates, and a light strip down each
      const plate = mesh(new THREE.BoxGeometry(len * .78, r * 1.5, .006), steel);
      plate.position.set(len / 2, 0, s * (r + .002));
      const strip = mesh(new THREE.BoxGeometry(len * .6, .006, .004), accents[1], false);
      strip.position.set(len / 2, 0, s * (r + .006));
      g.add(plate, strip);
    }
    return g;
  };

  const root = new THREE.Group();
  const base = cyl(.12, .06, dark);
  base.position.y = .03;
  const baseRing = ringAt(.116, accents[0]);
  baseRing.position.y = .061;
  const yaw = new THREE.Group();
  yaw.position.y = .06;
  root.add(yaw);
  const column = cyl(.055, .22, shell);
  column.position.y = .11;
  const collar = cyl(.068, .03, steel);
  collar.position.y = .015;
  if (!ghost) {   // the ghost is only what moves: drawn over the base it would z-fight
    root.add(base, baseRing);
    yaw.add(column, collar);
  }

  const j1 = new THREE.Group();
  j1.position.y = H0 - .06;
  yaw.add(j1);
  j1.add(cyl(.066, .14, steel, "z"));
  for (const s of [-1, 1]) { const r = ringAt(.058, accents[1], "z"); r.position.z = s * .071; j1.add(r); }
  j1.add(link(L1, .036, shell));

  const j2 = new THREE.Group();
  j2.position.x = L1;
  j1.add(j2);
  j2.add(cyl(.05, .11, steel, "z"));
  for (const s of [-1, 1]) { const r = ringAt(.043, accents[2], "z"); r.position.z = s * .056; j2.add(r); }
  j2.add(link(L2, .028, shell));

  const wrist = new THREE.Group();
  wrist.position.x = L2;
  j2.add(wrist);
  wrist.add(cyl(.036, .075, steel, "z"));
  const palm = mesh(new THREE.BoxGeometry(.04, .036, .11), dark);
  palm.position.x = .04;
  const wristRing = ringAt(.03, accents[3], "z");
  wristRing.position.z = .039;
  wrist.add(palm, wristRing);
  const fingers = [-1, 1].map(s => {
    const f = mesh(new THREE.BoxGeometry(.06, .02, .012), steel);
    f.position.set(.085, 0, s * .05);
    const tip = mesh(new THREE.BoxGeometry(.012, .021, .013), accents[3], false);
    tip.position.x = .03;
    f.add(tip);
    wrist.add(f);
    return f;
  });
  if (ghost) root.traverse(o => { o.renderOrder = 2; });

  return {
    root, accents,
    set(qs, grip = 0) {
      yaw.rotation.y = qs[0];
      j1.rotation.z = qs[1];
      j2.rotation.z = qs[2];
      wrist.rotation.z = -Math.PI / 2 - qs[1] - qs[2];
      fingers.forEach((f, i) => { f.position.z = (i ? 1 : -1) * (.05 - .018 * grip); });
    },
  };
}

const arm = makeArm();
const ghostMat = new THREE.MeshBasicMaterial({
  color: C.think, transparent: true, opacity: .16, depthWrite: false, blending: THREE.AdditiveBlending,
});
const ghost = makeArm(ghostMat);
scene.add(arm.root, ghost.root);

// --- the cup, the camera that watches it, the workspace ring, the base frame
const rimMat = glow(C.see, 2);
const cup = new THREE.Group();
{
  const glass = new THREE.MeshStandardMaterial({ color: 0x0f1a24, metalness: .3, roughness: .25, side: THREE.DoubleSide });
  const body = new THREE.Mesh(new THREE.CylinderGeometry(.036, .03, CUP_H, 48, 1, true), glass);
  body.position.y = CUP_H / 2;
  body.castShadow = true;
  const bottom = new THREE.Mesh(new THREE.CircleGeometry(.03, 32), glass);
  bottom.rotation.x = -Math.PI / 2;
  bottom.position.y = .002;
  const rim = new THREE.Mesh(new THREE.TorusGeometry(.036, .004, 10, 48), rimMat);
  rim.rotation.x = Math.PI / 2;
  rim.position.y = CUP_H;
  const band = new THREE.Mesh(new THREE.CylinderGeometry(.0335, .0325, .008, 48, 1, true), glow(C.see, 1.1));
  band.position.y = .035;
  cup.add(body, bottom, rim, band);
}
const hit = new THREE.Mesh(new THREE.CylinderGeometry(.09, .09, .22, 16), new THREE.MeshBasicMaterial({ visible: false }));
hit.position.y = .1;
cup.add(hit);
scene.add(cup);

const reach = ring(R_MAX, C.act);
const inner = ring(R_MIN, C.muted);
inner.material.opacity = .25;
scene.add(reach, inner);

const axes = new THREE.Group();   // robotics convention: x forward, y left, z up
[[1, 0, 0], [0, 0, -1], [0, 1, 0]].forEach(([x, y, z], i) => {
  const v = new THREE.Vector3(x, y, z);
  const from = i === 2 ? new THREE.Vector3(0, .062, 0) : v.clone().multiplyScalar(.13).setY(.003);
  const a = new THREE.ArrowHelper(v, from, i === 2 ? .16 : .14, C.muted, .022, .011);
  const m = a.line.material as THREE.LineBasicMaterial;
  m.transparent = true;
  m.opacity = .55;
  axes.add(a);
});
scene.add(axes);

const SENSOR = new THREE.Vector3(-.1, .34, -.8);
const sensorHead = new THREE.Group();
{
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(.012, .012, SENSOR.y, 12), metal(C.steel, .9, .3));
  mast.position.set(SENSOR.x, SENSOR.y / 2, SENSOR.z);
  mast.castShadow = true;
  const foot = new THREE.Mesh(new THREE.CylinderGeometry(.05, .06, .016, 32), metal(0x151b28, .6, .5));
  foot.position.set(SENSOR.x, .008, SENSOR.z);
  sensorHead.position.copy(SENSOR);
  const box = new THREE.Mesh(new THREE.BoxGeometry(.11, .05, .05), metal(C.shell));
  box.castShadow = true;
  const lens = new THREE.Mesh(new THREE.TorusGeometry(.016, .004, 10, 32), glow(C.see, 3));
  lens.position.z = .026;
  box.add(lens);
  sensorHead.add(box);
  scene.add(mast, foot, sensorHead);
}
const coneMat = new THREE.MeshBasicMaterial({
  color: C.see, transparent: true, opacity: .05, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
});
const coneLineMat = new THREE.LineBasicMaterial({ color: C.see, transparent: true, opacity: .5 });
const cone = new THREE.Mesh(new THREE.BufferGeometry(), coneMat);
const coneLines = new THREE.LineSegments(new THREE.BufferGeometry(), coneLineMat);
scene.add(cone, coneLines);

// the hand's path, fading behind it
const TRAIL = 120;
const trailPos = new Float32Array(TRAIL * 3), trailCol = new Float32Array(TRAIL * 3);
const trailGeo = new THREE.BufferGeometry();
trailGeo.setAttribute("position", new THREE.BufferAttribute(trailPos, 3));
trailGeo.setAttribute("color", new THREE.BufferAttribute(trailCol, 3));
const trail = new THREE.Line(trailGeo, new THREE.LineBasicMaterial({
  vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false,
}));
trail.frustumCulled = false;
scene.add(trail);

// --- kinematics
const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));

// the cup's floor position to joint angles, elbow up; says whether it's reachable
function ik(x: number, z: number): { q: Joints; why: Why } {
  const r = Math.hypot(x, z), h = WRIST_H;
  let d = Math.hypot(r, h), why: Why = "ok";
  if (d > L1 + L2 - 1e-4) { d = L1 + L2 - 1e-4; why = "far"; }
  else if (r < R_MIN) why = "near";
  const c2 = clamp((d * d - L1 * L1 - L2 * L2) / (2 * L1 * L2), -1, 1);
  const q2 = -Math.acos(c2);
  const q1 = Math.atan2(h, Math.max(r, 1e-6)) - Math.atan2(L2 * Math.sin(q2), L1 + L2 * Math.cos(q2));
  return { q: [Math.atan2(-z, x), clamp(q1, ...LIMITS[1]), clamp(q2, ...LIMITS[2])], why };
}

// joint angles to the fingertip position, in the scene's frame
function fk([a, q1, q2]: Joints): THREE.Vector3 {
  const r = L1 * Math.cos(q1) + L2 * Math.cos(q1 + q2), h = H0 + L1 * Math.sin(q1) + L2 * Math.sin(q1 + q2) - G;
  return new THREE.Vector3(r * Math.cos(a), h, -r * Math.sin(a));
}

// --- the scripted take: the cup is dragged to five places; the fourth is out of reach
const TAKE: [at: number, x: number, z: number][] = [[0, .40, .18], [3.0, .34, -.30], [6.0, -.2, -.4], [9.0, .70, .15], [12.0, .46, -.14]];
const MOVE = 1.1;
function scripted(t: number): Floor & { dragging: boolean } {
  let [, x, z] = TAKE[0], dragging = false;
  for (const [at, nx, nz] of TAKE.slice(1)) {
    const k = ease(span(t, at, at + MOVE));
    if (t >= at - .35 && t < at + MOVE + .25) dragging = true;
    x += (nx - x) * k;
    z += (nz - z) * k;
  }
  return { x, z, dragging };
}

// --- state, stepped at 240 Hz from wherever the last frame left it
interface State {
  t: number;
  q: Joints; v: Joints; want: Joints;
  why: Why;
  sensed: Floor | null; lastSense: number; senseFlash: number;
  grip: number; err: number;
  trail: THREE.Vector3[]; trailClock: number;
  cup: Floor; drag: boolean;
}
const user = { x: TAKE[0][1], z: TAKE[0][2], mode: "ik" as "ik" | "fk", fkq: [...REST] as Joints, speed: SPEED, dragging: false };
const fresh = (): State => ({
  t: 0, q: [...REST], v: [0, 0, 0], want: [...REST], why: "ok", sensed: null, lastSense: -1, senseFlash: 0,
  grip: 0, err: 0, trail: [], trailClock: 0, cup: { x: TAKE[0][1], z: TAKE[0][2] }, drag: false,
});
let S = fresh();
const fkMode = (): boolean => user.mode === "fk" && !recorded;
const speed = (): number => recorded ? SPEED : user.speed;

function step(): void {
  S.t += DT;
  const cupNow = recorded ? scripted(S.t) : { x: user.x, z: user.z, dragging: user.dragging };
  S.cup = cupNow;
  S.drag = cupNow.dragging;
  if (fkMode()) {
    S.want = [...user.fkq];
    S.why = "fk";
  } else if (S.t - S.lastSense >= 1 / SENSE_HZ) {   // 看: the camera samples the cup
    S.lastSense = S.t;
    S.senseFlash = 1;
    S.sensed = { x: cupNow.x, z: cupNow.z };
    const sol = ik(S.sensed.x, S.sensed.z);           // 想: inverse kinematics on what it saw
    S.want = sol.q;
    S.why = sol.why;
  }
  S.senseFlash = Math.max(0, S.senseFlash - DT * 6);
  const wn = speed(), vmax = .3 * wn;   // 动: each motor chases its angle, critically damped, speed-limited
  for (let i = 0; i < 3; i++) {
    const err = i === 0 ? wrap(S.want[i] - S.q[i]) : S.want[i] - S.q[i];
    S.v[i] = clamp(S.v[i] + (wn * wn * err - 2 * wn * S.v[i]) * DT, -vmax, vmax);
    S.q[i] = i === 0 ? wrap(S.q[i] + S.v[i] * DT) : S.q[i] + S.v[i] * DT;
  }
  const tip = fk(S.q);
  S.err = tip.distanceTo(new THREE.Vector3(cupNow.x, GRASP_Y, cupNow.z));
  const holding = !fkMode() && S.why === "ok" && S.err < .012 && !S.drag;
  S.grip = clamp(S.grip + (holding ? 1 : -1.5) * DT * 3, 0, 1);
  S.trailClock += DT;
  if (S.trailClock >= 1 / 60) {
    S.trailClock = 0;
    S.trail.push(tip);
    if (S.trail.length > TRAIL) S.trail.shift();
  }
}

// --- live controls
const controls = new OrbitControls(camera, canvas);
controls.target.set(.18, .16, 0);
Object.assign(controls, { enableDamping: true, minDistance: .8, maxDistance: 3.4, maxPolarAngle: 1.45, enabled: !recorded });
camera.position.set(1.05, .78, 1.28);

const NAMES: [zh: string, en: string][] = [["底座", "yaw θ₀"], ["肩", "shoulder θ₁"], ["肘", "elbow θ₂"]];
el("rows").innerHTML = NAMES.map(([zh, en], i) =>
  `<div class="row"><div class="name">${tr(zh)}<small>${en}</small></div><div></div>` +
  `<div class="vals"><span class="want" id="w${i}"></span> / <span class="have" id="h${i}"></span></div>` +
  `<div class="bar"><i class="want" id="bw${i}"></i><i class="have" id="bh${i}"></i></div></div>`).join("");
el("sliders").innerHTML = NAMES.map(([zh], i) =>
  `<label>${tr(zh)} <input type="range" id="s${i}" min="${LIMITS[i][0]}" max="${LIMITS[i][1]}" step=".01" value="${REST[i]}"></label>`).join("");
const sliders = NAMES.map((_, i) => el<HTMLInputElement>("s" + i));
sliders.forEach((s, i) => s.addEventListener("input", () => { user.fkq[i] = Number(s.value); }));
const speedInput = el<HTMLInputElement>("speed");
speedInput.addEventListener("input", () => {
  user.speed = Number(speedInput.value);
  el("speedVal").textContent = user.speed.toFixed(1);
});
el("mode").addEventListener("click", e => {
  const m = (e.target as HTMLElement).dataset.mode;
  if (m !== "ik" && m !== "fk") return;
  user.mode = m;
  if (m === "fk") { user.fkq = [...S.q]; sliders.forEach((s, i) => { s.value = String(S.q[i]); }); }
  document.querySelectorAll<HTMLButtonElement>("#mode button").forEach(b => b.classList.toggle("on", b.dataset.mode === m));
  document.body.classList.toggle("fkmode", m === "fk");
});
const showThink = el<HTMLInputElement>("showThink"), showSee = el<HTMLInputElement>("showSee");

// dragging the cup: along the floor, kept off the base and on the stage
const ray = new THREE.Raycaster(), ptr = new THREE.Vector2(), plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), onFloor = new THREE.Vector3();
const aim = (e: PointerEvent): void => {
  const r = canvas.getBoundingClientRect();
  ptr.set((e.clientX - r.left) / r.width * 2 - 1, -(e.clientY - r.top) / r.height * 2 + 1);
  ray.setFromCamera(ptr, camera);
};
canvas.addEventListener("pointerdown", e => {
  if (recorded || fkMode()) return;
  aim(e);
  if (!ray.intersectObject(hit).length) return;
  user.dragging = true;
  controls.enabled = false;
  canvas.setPointerCapture(e.pointerId);
  canvas.style.cursor = "grabbing";
});
canvas.addEventListener("pointermove", e => {
  if (recorded) return;
  aim(e);
  if (!user.dragging) { canvas.style.cursor = !fkMode() && ray.intersectObject(hit).length ? "grab" : ""; return; }
  if (!ray.ray.intersectPlane(plane, onFloor)) return;
  const r = Math.hypot(onFloor.x, onFloor.z), k = r > 1.1 ? 1.1 / r : r < .12 ? .12 / Math.max(r, 1e-6) : 1;
  user.x = onFloor.x * k;
  user.z = onFloor.z * k;
});
canvas.addEventListener("pointerup", () => { user.dragging = false; controls.enabled = true; canvas.style.cursor = ""; });

// --- one frame
const deg = (a: number): string => {
  const d = a * 180 / Math.PI;
  return (d >= 0 ? "+" : "−") + Math.abs(d).toFixed(1).padStart(5, "0") + "°";
};
const coords = (v: THREE.Vector3, n = 3): string => {   // scene (y up) to the robot's frame (x forward, y left, z up)
  const r: [string, number][] = [["x", v.x], ["y", -v.z], ["z", v.y]];
  return r.slice(0, n).map(([k, x]) => `${k} ${x >= 0 ? "+" : "−"}${Math.abs(x).toFixed(2)}`).join("  ") + " m";
};
const place = (e: HTMLElement, p: ScreenPoint, dx = 0, dy = 0): void => {
  e.style.left = p.x + dx + "px";
  e.style.top = p.y + dy + "px";
  e.style.opacity = p.front ? "1" : "0";
};

function frameAt(t: number): void {
  if (t < S.t - 1e-9) S = fresh();
  while (S.t + DT <= t + 1e-9) step();
  const fk_ = fkMode();

  arm.set(S.q, S.grip);
  ghost.set(S.want);
  // the plan shows while the arm hasn't caught up with it, and fades as it does
  const lag = Math.max(...S.want.map((w, i) => Math.abs(i === 0 ? wrap(w - S.q[i]) : w - S.q[i])));
  ghostMat.opacity = .2 * span(lag, .01, .12);
  ghost.root.visible = !fk_ && ghostMat.opacity > .003 && (recorded || showThink.checked);
  arm.accents.forEach((m, i) => {   // a joint glows brighter the harder its motor works
    (m as THREE.MeshStandardMaterial).emissiveIntensity = .8 + 2.4 * Math.abs(S.v[Math.min(i, 2)]) / (.3 * speed());
  });
  cup.position.set(S.cup.x, 0, S.cup.z);
  cup.visible = !fk_;
  const warn = S.why === "far" || S.why === "near";
  rimMat.color.set(warn ? C.amber : C.see);
  rimMat.emissive.set(warn ? C.amber : C.see);
  reach.material.color.set(S.why === "far" ? C.amber : C.act);
  reach.material.opacity = S.why === "far" ? .45 + .45 * Math.sin(t * 12) ** 2 : .5;

  // 看: the camera turns to what it last saw; its view is a pyramid onto the cup
  const seen = S.sensed ? new THREE.Vector3(S.sensed.x, CUP_H / 2, S.sensed.z) : new THREE.Vector3(.4, 0, 0);
  sensorHead.lookAt(seen);
  const see = !fk_ && (recorded || showSee.checked);
  cone.visible = coneLines.visible = see;
  if (see) {
    const toCam = SENSOR.clone().sub(seen).normalize();
    const side = new THREE.Vector3().crossVectors(toCam, new THREE.Vector3(0, 1, 0)).normalize().multiplyScalar(.06);
    const up = new THREE.Vector3().crossVectors(side, toCam).normalize().multiplyScalar(.06);
    const [a, b, c, d] = [side.clone().add(up), side.clone().sub(up), side.clone().negate().sub(up), side.clone().negate().add(up)].map(v => v.add(seen));
    const P = SENSOR;
    cone.geometry.setFromPoints([P, a, b, P, b, c, P, c, d, P, d, a]);
    coneLines.geometry.setFromPoints([P, a, P, b, P, c, P, d, a, b, b, c, c, d, d, a]);
    coneMat.opacity = .02 + .05 * S.senseFlash;
    coneLineMat.opacity = .18 + .4 * S.senseFlash;
  }

  S.trail.forEach((p, i) => {
    trailPos.set([p.x, p.y, p.z], i * 3);
    const k = (i / S.trail.length) ** 2 * .9;
    trailCol.set([.22 * k, .74 * k, .97 * k], i * 3);
  });
  trailGeo.setDrawRange(0, S.trail.length);
  trailGeo.attributes.position.needsUpdate = trailGeo.attributes.color.needsUpdate = true;

  if (recorded) {   // a slow orbit, a push in while the cup is out of reach, and back out
    const az = .62 + t * .035, elev = .44, dist = 2.05 + .12 * ease(span(t, 8.6, 10.2)) - .12 * ease(span(t, 12.2, 13.4));
    const o = controls.target.set(.16, .14, .04);
    camera.position.set(o.x + dist * Math.cos(elev) * Math.cos(az), o.y + dist * Math.sin(elev), o.z + dist * Math.cos(elev) * Math.sin(az));
    camera.lookAt(o);
  } else controls.update();

  render();
  hud(t, warn, fk_);
}

function hud(t: number, warn: boolean, fk_: boolean): void {
  el("clock").textContent = "T+" + t.toFixed(2).padStart(5, "0");
  el("dot").style.opacity = (t * 2 | 0) % 2 ? ".3" : "1";
  el("modeTag").textContent = fk_ ? "FORWARD KINEMATICS" : "INVERSE KINEMATICS";
  for (let i = 0; i < 3; i++) {
    el("w" + i).textContent = deg(S.want[i]);
    el("h" + i).textContent = deg(S.q[i]);
    const [lo, hi] = LIMITS[i], at = (a: number) => (clamp((a - lo) / (hi - lo), 0, 1) * 100).toFixed(1) + "%";
    el("bw" + i).style.left = at(S.want[i]);
    el("bh" + i).style.left = at(S.q[i]);
  }
  el("eePos").textContent = coords(fk(S.q));

  const moving = Math.max(...S.v.map(Math.abs)) > .02;
  el("stepSee").classList.toggle("on", !fk_ && S.senseFlash > .3);
  el("stepThink").classList.toggle("on", !fk_ && S.senseFlash > .15);
  el("stepAct").classList.toggle("on", moving);
  el("seeTxt").textContent = fk_ ? tr("不用看") : S.sensed ? tr("杯子 {0}", coords(new THREE.Vector3(S.sensed.x, 0, S.sensed.z), 2)) : tr("相机 15 Hz");
  el("thinkTxt").textContent = fk_ ? tr("你给关节角") : S.why === "far" ? tr("无解：太远了") : S.why === "near" ? tr("太近：会撞到底座") : tr("逆运动学 · 有解");
  el("actTxt").textContent = fk_ ? tr("电机跟随滑块") : tr("离杯子 {0} mm", (S.err * 1000).toFixed(0));
  const [cls, head, detail] = fk_ ? ["busy", "FK", tr("关节角 → 手的位置")]
    : S.why === "far" ? ["warn", "! OUT OF REACH", tr("超出 {0} cm", ((Math.hypot(S.cup.x, S.cup.z) - R_MAX) * 100).toFixed(0))]
    : S.why === "near" ? ["warn", "! TOO CLOSE", tr("太靠近底座")]
    : S.grip > .6 ? ["ok", "✓ REACHED", tr("够到了")]
    : ["busy", "▸ MOVING", tr("电机转动中")];
  const status = el("status");
  status.className = "status " + cls;
  status.innerHTML = `${head}<small>${detail}</small>`;

  const box = el("cupBox"), tag = el("cupTag"), cursor = el("cursor");
  box.style.display = tag.style.display = fk_ ? "none" : "";
  cursor.style.display = recorded && S.drag ? "" : "none";
  if (!fk_) {
    const c = toScreen(new THREE.Vector3(S.cup.x, CUP_H / 2, S.cup.z), camera, canvas);
    const top = toScreen(new THREE.Vector3(S.cup.x, CUP_H * 1.25, S.cup.z), camera, canvas);
    const size = Math.max(40, (c.y - top.y) * 3.2);
    box.style.width = box.style.height = size + "px";
    place(box, c);
    place(tag, c, 0, -size / 2 - 8);
    box.classList.toggle("warn", warn);
    tag.classList.toggle("warn", warn);
    tag.textContent = warn ? (S.why === "far" ? tr("TARGET · 够不着") : tr("TARGET · 太近")) : S.grip > .6 ? tr("TARGET · 已够到") : "TARGET · CUP";
    place(cursor, toScreen(new THREE.Vector3(S.cup.x, CUP_H, S.cup.z), camera, canvas), 6, -4);
  }
  // the reach label sits on the side of the ring nearest the camera
  const az = Math.atan2(camera.position.z, camera.position.x) - .5;
  const reachTag = el("reachTag");
  place(reachTag, toScreen(new THREE.Vector3(Math.cos(az) * R_MAX, 0, Math.sin(az) * R_MAX), camera, canvas), 0, 26);
  reachTag.textContent = `REACH ${R_MAX.toFixed(2)} m`;
  const ends = [new THREE.Vector3(.29, .003, 0), new THREE.Vector3(0, .003, -.29), new THREE.Vector3(0, .25, 0)];
  ["axX", "axY", "axZ"].forEach((id, i) => place(el(id), toScreen(ends[i], camera, canvas)));
}

run(frameAt, { dur: Number(q.get("dur") || 14), still: 2.4 });
